/**
 * Curriculum attribution for Global / Group / User words.
 * Unattributed placeholders (empty, N/A, Student's Words, Imported, …)
 * normalize to course = "Other" with no module/lesson.
 */

export const OTHER_COURSE = 'Other';

const UNATTRIBUTED_ALIASES = new Set([
    '',
    'n/a',
    'na',
    'other',
    "student's words",
    'students words',
    'imported',
]);

export function isUnattributedCourse(course) {
    return UNATTRIBUTED_ALIASES.has(String(course ?? '').trim().toLowerCase());
}

export function isOtherCourse(course) {
    return String(course ?? '').trim().toLowerCase() === 'other';
}

/** Words the student added themselves (Wordlist paste) — not part of curriculum training pipelines. */
export function isStudentAddedWord(word) {
    if (!word || typeof word !== 'object') return false;
    return word.student_added === true || word.source === 'student';
}

/** Canonical course key for filters (maps legacy aliases → Other). */
export function canonicalWordCourse(course) {
    const raw = String(course ?? '').trim();
    if (!raw || isUnattributedCourse(raw)) return OTHER_COURSE;
    return raw;
}

/**
 * If the word has no real curriculum course, set course=Other and clear module/lesson.
 * Leaves real course attributions unchanged.
 */
export function normalizeWordAttribution(word) {
    if (!word || typeof word !== 'object') return word;
    if (!isUnattributedCourse(word.course)) return word;
    return {
        ...word,
        course: OTHER_COURSE,
        module: null,
        lesson: null,
    };
}

/** Ensure Courses list includes Other (for filters / dropdowns). */
export function ensureOtherCourseEntry(courses = []) {
    const list = Array.isArray(courses) ? [...courses] : [];
    if (list.some((c) => isOtherCourse(c?.name))) return list;
    list.push({
        id: '__virtual_other_course__',
        name: OTHER_COURSE,
        description: 'Words without curriculum attribution',
        color: '#94a3b8',
        modules: 0,
        isVirtual: true,
    });
    return list;
}

/**
 * Match a word row against DatabaseFilter course state.
 * Selecting Other with no modules checked = all Other / unattributed words.
 * Curriculum courses still require at least one module checkbox.
 */
export function rowMatchesCourseFilter(row, cFilter) {
    if (!cFilter || Object.keys(cFilter).length === 0) return true;

    const rawCourse = String(row?.course ?? '').trim();
    const courseKey = canonicalWordCourse(rawCourse);
    // Do not fall back to Other for curriculum rows — that incorrectly widens the filter.
    const cState = courseKey === OTHER_COURSE
        ? (cFilter[OTHER_COURSE] || cFilter[rawCourse])
        : (cFilter[courseKey] || cFilter[rawCourse]);
    if (!cState) return false;

    const modules = cState.modules || [];
    if (modules.length === 0) {
        return isOtherCourse(courseKey) || isUnattributedCourse(rawCourse);
    }

    const m = Number(row?.module);
    if (Number.isNaN(m) || !modules.includes(m)) return false;

    const l = Number(row?.lesson);
    const modLessons = cState.lessons?.[m];
    if (modLessons && modLessons.length > 0 && !Number.isNaN(l) && !modLessons.includes(l)) {
        return false;
    }
    return true;
}
